import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Snackbar,
  Alert,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { onAuthStateChanged } from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
  doc,
} from "firebase/firestore";
import { auth, firestore } from "../../../firebase";

const INITIAL_FORM = {
  facilityName: "",
  branchName: "",
  adminName: "",
  phoneNumber: "",
  email: "",
  region: "",
};

const normalizeRoleKey = (role) =>
  String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const normalizeEmailValue = (email) => String(email || "").trim().toLowerCase();

const ManageFacilityPage = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingFacilityAdminId, setEditingFacilityAdminId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [facilityAdmins, setFacilityAdmins] = useState([]);
  const [loadingFacilityAdmins, setLoadingFacilityAdmins] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user?.email) {
        setCurrentAdmin(null);
        return;
      }

      try {
        const q = query(
          collection(firestore, "UserProfileMaster"),
          where("email", "==", user.email)
        );
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const adminDoc = snapshot.docs[0];
          setCurrentAdmin({ id: adminDoc.id, uid: user.uid, ...adminDoc.data() });
        } else {
          // Fallback for profiles stored using uid as document id.
          const profileByUid = await getDoc(doc(firestore, "UserProfileMaster", user.uid));
          if (profileByUid.exists()) {
            setCurrentAdmin({ id: profileByUid.id, uid: user.uid, ...profileByUid.data() });
          } else {
            setCurrentAdmin(null);
          }
        }
      } catch (error) {
        console.error("Error fetching admin profile:", error);
      }
    });

    return () => unsubscribe();
  }, []);

  // Listener for facility admins
  useEffect(() => {
    if (!currentAdmin?.tenantId) {
      setFacilityAdmins([]);
      setLoadingFacilityAdmins(false);
      return;
    }

    setLoadingFacilityAdmins(true);

    const facilityQuery = query(
      collection(firestore, "UserProfileMaster"),
      where("tenantId", "==", currentAdmin.tenantId)
    );

    const unsubscribeFacility = onSnapshot(
      facilityQuery,
      (snapshot) => {
        const facilityList = snapshot.docs
          .map((entry) => ({
            id: entry.id,
            ...entry.data(),
          }))
          .filter((entry) => normalizeRoleKey(entry.role) === "facility_admin");
        
        setFacilityAdmins(facilityList);
        setLoadingFacilityAdmins(false);
      },
      (error) => {
        console.error("Error fetching facility admins:", error);
        setLoadingFacilityAdmins(false);
      }
    );

    return () => unsubscribeFacility();
  }, [currentAdmin?.tenantId]);

  const handleFieldChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCreateFacilityAdmin = async () => {
    const { facilityName, branchName, adminName, phoneNumber, email, region } = formData;

    if (
      !facilityName?.trim() ||
      !branchName?.trim() ||
      !adminName?.trim() ||
      !phoneNumber?.trim() ||
      !email?.trim() ||
      !region?.trim()
    ) {
      showSnackbar("All fields are required", "error");
      return;
    }

    if (!currentAdmin?.tenantId) {
      showSnackbar("Tenant admin context not found", "error");
      return;
    }

    setSaving(true);

    try {
      const normalizedEmail = normalizeEmailValue(email);
      const tenantUsersQuery = query(
        collection(firestore, "UserProfileMaster"),
        where("tenantId", "==", currentAdmin.tenantId)
      );
      const tenantUsersSnapshot = await getDocs(tenantUsersQuery);
      const existingTenantUserDoc = tenantUsersSnapshot.docs.find(
        (entry) => normalizeEmailValue(entry.data()?.email) === normalizedEmail
      );

      const facilityPayload = {
        role: "facility_admin",
        tenantId: currentAdmin.tenantId,
        displayName: adminName.trim(),
        adminName: adminName.trim(),
        facilityName: facilityName.trim(),
        branchName: branchName.trim(),
        region: region.trim(),
        phoneNumber: phoneNumber.trim(),
        email: normalizedEmail,
        status: "Active",
        updatedAt: new Date(),
        updatedBy: currentAdmin.uid,
      };

      if (isEditMode && editingFacilityAdminId) {
        // Edit existing facility admin
        await updateDoc(
          doc(firestore, "UserProfileMaster", editingFacilityAdminId),
          facilityPayload
        );
        showSnackbar("Facility admin updated successfully");
        setIsEditMode(false);
        setEditingFacilityAdminId(null);
      } else if (existingTenantUserDoc) {
        await updateDoc(doc(firestore, "UserProfileMaster", existingTenantUserDoc.id), facilityPayload);
        showSnackbar("Facility admin updated successfully");
      } else {
        await addDoc(collection(firestore, "UserProfileMaster"), {
          ...facilityPayload,
          createdAt: new Date(),
          createdBy: currentAdmin.uid,
        });
        showSnackbar("New facility admin is created successfully");
      }

      setFormData(INITIAL_FORM);
      setShowCreateForm(false);
    } catch (error) {
      console.error("Error creating facility admin:", error);
      showSnackbar("Failed to create facility admin. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  const formatCreatedAt = (createdAt) => {
    if (!createdAt) return "-";
    const dateValue = createdAt?.toDate ? createdAt.toDate() : new Date(createdAt);
    if (Number.isNaN(dateValue.getTime())) return "-";
    return dateValue.toLocaleDateString();
  };

  const handleDeleteFacilityAdmin = async (facilityAdminId) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this facility admin?");
    if (!isConfirmed) return;

    try {
      await deleteDoc(doc(firestore, "UserProfileMaster", facilityAdminId));
      showSnackbar("Facility admin deleted successfully");
    } catch (error) {
      console.error("Error deleting facility admin:", error);
      showSnackbar("Failed to delete facility admin. Please try again.", "error");
    }
  };

  const handleEditFacilityAdmin = (facilityAdmin) => {
    setFormData({
      facilityName: facilityAdmin.facilityName || "",
      branchName: facilityAdmin.branchName || "",
      adminName: facilityAdmin.adminName || facilityAdmin.displayName || "",
      phoneNumber: facilityAdmin.phoneNumber || "",
      email: facilityAdmin.email || "",
      region: facilityAdmin.region || "",
    });
    setEditingFacilityAdminId(facilityAdmin.id);
    setIsEditMode(true);
    setShowCreateForm(true);
  };

  return (
    <Box sx={{ bgcolor: "#f5f7fb", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="xl">
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Manage Facility
          </Typography>
          <Button
            variant="contained"
            onClick={() => setShowCreateForm(true)}
            sx={{
              textTransform: "none",
              px: 3,
              borderRadius: 2,
              background: "linear-gradient(90deg, #2196F3 0%, #9C27B0 100%)",
            }}
          >
            Create Facility Admin
          </Button>
        </Box>

        <Card sx={{ mt: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Facility Management
            </Typography>

            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Facility Name</TableCell>
                    <TableCell>Branch Name</TableCell>
                    <TableCell>Admin Name</TableCell>
                    <TableCell>Phone Number</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loadingFacilityAdmins && facilityAdmins.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No facility admins found for this tenant.
                      </TableCell>
                    </TableRow>
                  )}

                  {loadingFacilityAdmins && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        Loading facility admins...
                      </TableCell>
                    </TableRow>
                  )}

                  {!loadingFacilityAdmins &&
                    facilityAdmins.map((facilityAdmin) => (
                      <TableRow key={facilityAdmin.id}>
                        <TableCell>{facilityAdmin.facilityName || "-"}</TableCell>
                        <TableCell>{facilityAdmin.branchName || "-"}</TableCell>
                        <TableCell>{facilityAdmin.adminName || facilityAdmin.displayName || "-"}</TableCell>
                        <TableCell>{facilityAdmin.phoneNumber || "-"}</TableCell>
                        <TableCell>{facilityAdmin.email || "-"}</TableCell>
                        <TableCell>{facilityAdmin.region || "-"}</TableCell>
                        <TableCell>{facilityAdmin.role || "-"}</TableCell>
                        <TableCell>{formatCreatedAt(facilityAdmin.createdAt)}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleEditFacilityAdmin(facilityAdmin)}
                            aria-label="Edit facility admin"
                            sx={{ mr: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDeleteFacilityAdmin(facilityAdmin.id)}
                            aria-label="Delete facility admin"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Dialog
          open={showCreateForm}
          onClose={() => {
            if (saving) return;
            setShowCreateForm(false);
            setFormData(INITIAL_FORM);
            setIsEditMode(false);
            setEditingFacilityAdminId(null);
          }}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              width: "760px",
              maxWidth: "95vw",
              borderRadius: 1,
            },
          }}
        >
          <DialogTitle sx={{ fontSize: "2rem", fontWeight: 500, pt: 3, px: 4 }}>
            {isEditMode ? "Edit Facility Admin" : "Create Facility Admin"}
          </DialogTitle>
          <DialogContent sx={{ px: 4, pb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Facility Name"
                  required
                  fullWidth
                  value={formData.facilityName}
                  onChange={handleFieldChange("facilityName")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Branch Name"
                  required
                  fullWidth
                  value={formData.branchName}
                  onChange={handleFieldChange("branchName")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Admin Name"
                  required
                  fullWidth
                  value={formData.adminName}
                  onChange={handleFieldChange("adminName")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Phone No"
                  required
                  fullWidth
                  value={formData.phoneNumber}
                  onChange={handleFieldChange("phoneNumber")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  type="email"
                  required
                  fullWidth
                  value={formData.email}
                  onChange={handleFieldChange("email")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Region"
                  required
                  fullWidth
                  value={formData.region}
                  onChange={handleFieldChange("region")}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 4, pb: 3, gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setShowCreateForm(false);
                setFormData(INITIAL_FORM);
                setIsEditMode(false);
                setEditingFacilityAdminId(null);
              }}
              disabled={saving}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateFacilityAdmin}
              disabled={saving}
              sx={{
                textTransform: "none",
                px: 3,
                borderRadius: 2,
                background: "linear-gradient(90deg, #2196F3 0%, #9C27B0 100%)",
              }}
            >
              {saving ? "Saving..." : isEditMode ? "Update Facility Admin" : "Save Facility Admin"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageFacilityPage;
